<template>
  <div>
    <BoxWithTitle>
      <RedirectModal
        :show="showRedirectModal"
        @close="showRedirectModal = false"
      />
    </BoxWithTitle>
    <v-toolbar
      dark
      color="primary"
      class="bloc"
    >
      <v-toolbar-title>
        {{ networkName }}
      </v-toolbar-title>
      <v-btn
        color="primary"
        @click="onClickSave"
      >
        {{ $t('forms.btn.save') }}
      </v-btn>
    </v-toolbar>
    <v-btn
      class="siteTree-btn m-4"
      color="secondary"
      @click="showAll = !showAll"
    >
      {{ showAll ? $t('tree.collapseAll') : $t('tree.expandAll') }}
    </v-btn>
    <v-progress-linear
      v-if="loading"
      indeterminate
    />
    <div
      v-else
      class="content"
    >
      <Categories
        v-for="(category, index) in data.categories"
        :key="index"
        class="mb-2"
        :show="showAll"
        :isEdition="true"
        :frequencies="frequencies"
        :category="category"
        @updateData="updateData(index, $event)"
      />
      <div class="mb-2">
        <v-toolbar
          dark
          dense
          color="primary"
        >
          <div class="commentStyle">
            <v-icon class="commentStyleIcon">
              mdi-message-draw
            </v-icon>
            <span class="commentStyleLabel">{{ $t('forms.labels.comment').toUpperCase() }}</span>
          </div>
        </v-toolbar>
        <v-container>
          <GlobalComment
            position="opening"
            :comment="{message: data.comment}"
            :showAddSentences="false"
            :canUploadImages="false"
            :showSaveBtn="false"
            @commentChange="data.comment = $event"
          />
        </v-container>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { cloneDeep, isEmpty } from 'lodash';
import Categories from '@/views/maintenanceSchedule/Categories';
// eslint-disable-next-line import/no-cycle
import lockService from '@/utilities/lockService';
import RedirectModal from '@/components/shared/RedirectModal.vue';
import GlobalComment from '@/views/laboratory/workOrder/summary/GlobalComment.vue';


export default {
    components: {
        GlobalComment,
        RedirectModal,
        Categories,
    },
    data() {
        return {
            showRedirectModal: false,
            showAll: true,
            frequencies: [],
            loading: false,
            isNew: true,
            actionName: 'schedule',
            data: {
                comment: null,
                network: null,
                categories: [],
            },
        };
    },
    computed: {
        ...mapGetters([
            'network',
            'component',
            'hasPermission',
        ]),
        networkName() {
            const networkName = `${this.network.name} (${this.network.type.key})`;
            if (!isEmpty(this.component)) {
                return `${networkName} -> ${this.component.name} (${this.component.type.key})`;
            }
            return networkName;
        },
    },
    mounted() {
        window.addEventListener('beforeunload', this.handleUnload);
    },
    async beforeRouteLeave(to, from, next) {
        try {
            if (this.component && this.component.id) {
                const releaseStatus = await lockService.releaseLock(this.component.id, this.actionName);
                if (!releaseStatus) return next(false);
            }
        } catch (error) {
            console.error('Error releasing lock on route leave:', error);
            // Continue navigation even if lock release fails
        }
        return next();
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.handleUnload);
        // Ensure locks are released even if beforeRouteLeave didn't complete
        if (this.component && this.component.id) {
            lockService.releaseLock(this.component.id, this.actionName)
                .catch(err => console.error('Error releasing lock on destroy:', err));
        }
    },

    async created() {
        await this.getSchedules();
        await this.getFrequencies();
        if (!this.$store.getters.lockFrom) {
            await this.checkLockOnLoad();
        }
    },
    methods: {
        async getScheduleTemplate() {
            const queryParams = { networkId: this.network.id };
            if (this.component.id) { queryParams.componentId = this.component.id; }
            const { data } = await this.$rq.getScheduleTemplate(queryParams);
            this.data.categories = data.categories;
        },
        handleUnload() {
            if (this.component && this.component.id) {
                // Handle synchronously for page unload events
                try {
                    lockService.releaseLock(this.component.id, this.actionName);
                } catch (error) {
                    console.error('Error releasing lock on unload:', error);
                }
            }
        },
        async checkLockOnLoad() {
            try {
                const lockAvailable = await lockService.getLockStatus(this.component.id, this.actionName);
                if (!lockAvailable) {
                    this.showRedirectModal = true;
                    return;
                }
                await lockService.requestLock(this.component.id, this.actionName);
            } catch (error) {
                this.showRedirectModal = true;
            }
        },
        async getSchedules() {
            this.loading = true;
            if (this.network.id) {
                const queryParams = { networkId: this.network.id };
                if (this.component.id) { queryParams.componentId = this.component.id; }
                const { data } = await this.$rq.getSchedules(queryParams);
                if (data.length > 0) {
                    this.isNew = false;
                    [this.data] = data;
                    await this.getSchedule();
                } else {
                    await this.getScheduleTemplate();
                }
            }
            this.loading = false;
        },
        async getSchedule() {
            const { data } = await this.$rq.getSchedule(this.data.id);
            this.data = data;
        },
        async getFrequencies() {
            const { data } = await this.$rq.getFrequencies();
            /* Convert objet in string for component VSelect */
            const newData = data.frequencies.map(frequency => ({
                ...frequency,
            }));

            this.frequencies = cloneDeep(newData);
        },
        async onClickSave() {
            if (this.isNew) {
                await this.addSchedule();
            } else {
                await this.updateSchedule();
            }
        },
        async saveSchedule(action) {
            const finalData = {
                ...this.data,
                comment: this.isHtmlContentEmpty(this.data.comment) ? '' : this.data.comment,
            };
            const payload = this.preparePayload(finalData);

            if (action === 'add') {
                await this.$rq.addSchedule(payload);
                this.$toasted.global.toast_success({
                    message: this.$t('msg.scheduleCreated'),
                });
            }
            if (action === 'update') {
                await this.$rq.updateSchedule(this.data.id, payload);
                this.$toasted.global.toast_success({
                    message: this.$t('msg.scheduleUpdated'),
                });
            }
        },
        async addSchedule() {
            await this.saveSchedule('add');
        },

        async updateSchedule() {
            await this.saveSchedule('update');
        },
        isHtmlContentEmpty(html) {
            const div = document.createElement('div');
            div.innerHTML = html;
            const textContent = div.textContent || div.innerText || '';
            return textContent.trim().length === 0;
        },
        updateData(index, category) {
            this.data.categories[index] = category;
        },
        preparePayload({ categories, ...payload }) {
            const data = {
                ...cloneDeep(payload),
                network: this.network.id,
                categories: categories.map(category => this.preparePayloadCategory(category)),
            };
            if (this.component.id) { data.component = this.component.id; }
            return data;
        },
        preparePayloadCategory({ activities, name, ...attrs }) {
            return {
                ...attrs,
                activities: activities.map(activity => this.preparePayloadActivity(activity)),
            };
        },
        preparePayloadActivity({ frequency, name, procedure, ...attrs }) {
            return {
                ...attrs,
                frequency: frequency.id,
            };
        },
    },
};
</script>
<style lang="scss" scoped>

@media screen and (max-width: 960px) {
  .content{
    overflow-x: scroll !important
  }
}

::v-deep .v-toolbar__content {
    justify-content: space-between !important;
}

.commentStyle{
    display: flex;
    column-gap: .5rem;
    &Label{
        font-size: 15px;
        font-weight: 500;
    }
    &Icon{
        font-size: 20px;
        margin-left: -.5rem;
    }
}
</style>
